import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  circ,
  facing,
  fillet,
  fit,
  prism,
  proj,
  rad,
  unproj,
  type Ring,
  type Vec2,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  mk,
  pointer,
  put,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
  type Solid,
} from "./stage";

/**
 * Wheel: a pie chart built as a solid, a thick round wheel on a low board, cut
 * into six wedges of uneven size. The pointer's angle round the centre picks a
 * wedge: it slides out from the centre, and its neighbours slide out after it,
 * less the further round, staggered outwards on the 700ms lift curve. At rest
 * one wedge stands a little proud, bright, with the cuts showing between all
 * six. The slider is how far the wedge comes out.
 *
 * The pattern: one of many, as Riffle, round a centre. Tweens, a stagger and a
 * falloff by distance round the wheel, a hit test on the plane of the wheel's
 * top, which never moves, and a paint order that holds in every pose: a wedge
 * only ever slides along its own bisector, away from the cuts beside it.
 */

const SHARE = [0.24, 0.18, 0.12, 0.2, 0.09, 0.17],
  N = SHARE.length;
const RW = 66,
  RB = 90,
  ZH = 18,
  CUT = 1,
  START = -100,
  STEP = 45;
/** At rest: how far each wedge stands out of the wheel, and the one that says where to look. */
const REST = [0, 1.5, 7, 1.5, 0, 0],
  MARK = 2;
/** The share of the pull a wedge takes at 0, 1, 2 and 3 wedges round from the one pulled. */
const FALL = [1, 0.3, 0.08, 0];
/** Each wedge's angles, in degrees, from START round the wheel. */
const SPAN = SHARE.map((_, k) => {
  const a0 = START + 360 * SHARE.slice(0, k).reduce((a, s) => a + s, 0);
  return [a0, a0 + 360 * SHARE[k]];
});

/**
 * A wedge's footprint as a ring: its two cut edges, each set CUT in from its
 * radius so the cuts show, and its arc, the three corners rounded; each
 * sample's normal points out of the wedge. Also the ring inset by b, its crease.
 */
function wedge([a0, a1]: number[], b: number): [Ring, Ring] {
  const h = rad(a1 - a0) / 2,
    mid = rad(a0 + a1) / 2;
  const apex: Vec2 = [
    Math.cos(mid) * (CUT / Math.sin(h)),
    Math.sin(mid) * (CUT / Math.sin(h)),
  ];
  const edge = (a: number, side: number): Vec2 => {
    const r = Math.sqrt(RW * RW - CUT * CUT),
      t = rad(a);
    return [
      r * Math.cos(t) - side * CUT * Math.sin(t),
      r * Math.sin(t) + side * CUT * Math.cos(t),
    ];
  };
  const e0 = edge(a0, 1),
    e1 = edge(a1, -1),
    arc: Vec2[] = [];
  const t0 = rad(a0) + 6 / RW,
    t1 = rad(a1) - 6 / RW,
    n = Math.max(2, Math.ceil((t1 - t0) / rad(2.5)));
  for (let i = 0; i <= n; i++) {
    const t = t0 + ((t1 - t0) * i) / n;
    arc.push([RW * Math.cos(t), RW * Math.sin(t)]);
  }
  const pts = fillet(
    [apex, e0, ...arc, e1],
    [4, 3, ...arc.map(() => 0), 3],
  ).filter(
    (p, i, all) =>
      i === 0 || Math.hypot(p[0] - all[i - 1][0], p[1] - all[i - 1][1]) > 1e-6,
  );
  const ring: Ring = pts.map((p, i) => {
    const a = pts[(i + pts.length - 1) % pts.length],
      c = pts[(i + 1) % pts.length];
    const tx = c[0] - a[0],
      ty = c[1] - a[1],
      l = Math.hypot(tx, ty);
    return { u: p[0], v: p[1], nu: ty / l, nv: -tx / l };
  });
  return [
    ring,
    ring.map((q) => ({
      u: q.u - q.nu * b,
      v: q.v - q.nv * b,
      nu: q.nu,
      nv: q.nv,
    })),
  ];
}

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let pull = value;

  const C = Cam(45, 0.5, 1.6),
    pts: [number, number, number][] = [];
  for (let a = 0; a < 360; a += 15) {
    const c = Math.cos(rad(a)),
      s = Math.sin(rad(a));
    pts.push([RB * c, RB * s, -5], [(RW + 22) * c, (RW + 22) * s, ZH]);
  }
  fit(C, pts, 200, 166);
  const P = proj(C),
    front = facing(C);

  const g = mk("g", {}, svg);
  put(solid(g), prism(P, front, circ(RB, 160), circ(RB - 1.8, 160), -5, 0));

  // Back to front: a wedge whose bisector points further from the camera's
  // side (225°, away from +x+y) is behind. Wedges only slide outwards along
  // their bisectors, so this order holds in every pose.
  const away = (k: number) =>
    Math.abs(
      (((((SPAN[k][0] + SPAN[k][1]) / 2 - 225) % 360) + 540) % 360) - 180,
    );
  const wedges = SHARE.map((share, k) => {
    const [ring, crease] = wedge(SPAN[k], 1);
    const mid = rad((SPAN[k][0] + SPAN[k][1]) / 2);
    return {
      k,
      share,
      ring,
      crease,
      dx: Math.cos(mid),
      dy: Math.sin(mid),
      d: tween(REST[k]),
      el: null as Solid,
      drawn: NaN,
    };
  });
  wedges
    .slice()
    .sort((p, q) => away(p.k) - away(q.k))
    .forEach((w) => {
      w.el = solid(g);
    });

  const shift = (ring: Ring, x: number, y: number): Ring =>
    ring.map((q) => ({ u: q.u + x, v: q.v + y, nu: q.nu, nv: q.nv }));
  function draw(w, d) {
    if (d === w.drawn) return;
    w.drawn = d;
    put(
      w.el,
      prism(
        P,
        front,
        shift(w.ring, w.dx * d, w.dy * d),
        shift(w.crease, w.dx * d, w.dy * d),
        0,
        ZH,
      ),
    );
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const w of wedges) {
      draw(w, tval(w.d, now));
      if (!tdone(w.d, now)) moving = true;
    }
    return moving;
  });
  bag.add(() => B.unregister());

  /** The wedge under a pointer on the plane of the wheel's top, by its angle against the wedges' rest angles; -1 off the wheel. */
  function hit([sx, sy]) {
    const [wx, wy] = unproj(C, sx, sy, ZH);
    if (Math.hypot(wx, wy) > RW + 24) return -1;
    const a = ((Math.atan2(wy, wx) * 180) / Math.PI - START + 720) % 360;
    return SPAN.findIndex(([a0, a1]) => a >= a0 - START && a < a1 - START);
  }

  let act = -1;
  /** Pulls wedge a (-1 puts them all back): each takes its share of the pull, later the further round it is from the one pulled. */
  function setActive(a) {
    if (a === act) return;
    const now = performance.now(),
      from = a >= 0 ? a : act;
    act = a;
    for (const w of wedges) {
      const far = Math.min(Math.abs(w.k - from), N - Math.abs(w.k - from));
      tset(
        w.d,
        a < 0 ? REST[w.k] : pull * FALL[Math.min(far, 3)],
        now,
        far * STEP,
      );
      w.el.sil.classList.toggle("hi", a < 0 ? w.k === MARK : w.k === a);
    }
    read.textContent =
      a < 0 ? "rest" : `slice ${a + 1} · ${Math.round(SHARE[a] * 100)}%`;
    B.wake();
  }

  wedges[MARK].el.sil.classList.add("hi");
  bag.add(
    pointer(stage, {
      move: (p) => setActive(hit(p)),
      leave: () => setActive(-1),
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      pull = v;
      if (act >= 0) {
        const a = act;
        act = -1;
        setActive(a);
      }
    },
    destroy: () => bag.dispose(),
  };
}

export const WHEEL: HairlineFigure = {
  name: "wheel",
  means:
    "A pie chart cut from a solid wheel: the slice under the pointer slides out, and the slices beside it follow, less the further round.",
  range: [8, 15, 22],
  tour: [[170, 178], [157, 126], [263, 143], null],
  mount,
};
